import React, { useEffect, useState } from "react";
import { api, getToken, setToken } from "../lib/api";
import { Input } from "../components/ui/input";
import { Lock, LogOut, Eye, EyeOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useParams, Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FAQS, LEGAL } from "../mock";
import { MoneySummary, useBookingFilter, PromoCodesPanel, DeleteBookingButton } from "../components/site/AdminTools";
import { RidesPanel } from "../components/site/AdminRides";
import { ContentEditor } from "../components/site/AdminContent";

const SimpleHero = ({ eyebrow, title, sub }) => (
  <section className="bg-[#1A3C37] text-[#F7F3EA]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16 fade-up">
      <p className="text-[13px] tracking-[0.25em] uppercase text-[#7FB8C4]">{eyebrow}</p>
      <h1 className="font-serif-d font-bold text-[30px] sm:text-[54px] mt-2 sm:mt-3">{title}</h1>
      {sub && <p className="mt-3 text-[17px] text-[#F7F3EA]/80">{sub}</p>}
    </div>
  </section>
);

export const Faq = () => (
  <div data-testid="faq-page">
    <SimpleHero eyebrow="Help centre" title="Frequently asked questions" sub="Everything you need to know before you travel." />
    <section className="max-w-3xl mx-auto px-6 py-16">
      <Accordion type="single" collapsible className="bg-white rounded-2xl border border-[#183C35]/10 px-6">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`i${i}`} className="border-[#183C35]/10">
            <AccordionTrigger className="text-left text-[17px] font-medium text-[#183C35] hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-[15px] text-[#202522]/75 leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <p className="mt-8 text-[15px] text-[#202522]/70">Still have questions? <Link to="/contact" className="text-[#3E7F8E] font-medium hover:text-[#183C35]">Contact us</Link></p>
    </section>
  </div>
);

export const Legal = () => {
  const { page } = useParams();
  const [title, body] = LEGAL[page] || LEGAL.privacy;
  return (
    <div data-testid="legal-page">
      <SimpleHero eyebrow="Legal" title={title} />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-[17px] leading-[1.8] text-[#202522]/80">{body}</p>
        <p className="mt-6 text-[14px] text-[#202522]/55">Last updated: October 2026</p>
      </section>
    </div>
  );
};

const load = (k) => JSON.parse(localStorage.getItem(k) || "[]");

const AdminDashboard = ({ onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const messages = load("aloha_messages");
  const [tours, setTours] = useState([]);
  const togglePaid = async (b) => {
    const next = b.payment_status === "paid" ? "pending" : "paid";
    try {
      const { data } = await api.patch(`/bookings/${b.ref}/payment`, { payment_status: next });
      setBookings((list) => list.map((x) => (x.ref === b.ref ? data : x)));
      if (next === "paid") {
        toast.success(`Marked paid · receipt emailed to ${b.email}`);
        setTimeout(() => api.get(`/bookings/${b.ref}`).then((r) => setBookings((list) => list.map((x) => (x.ref === b.ref ? r.data : x)))).catch(() => {}), 6000);
      } else toast("Marked as awaiting payment");
    } catch { toast.error("Could not update payment status"); }
  };
  const [sendingReview, setSendingReview] = useState(null);
  const sendReview = async (b) => {
    if (!window.confirm(`Send the review & discount email to ${b.email}?`)) return;
    setSendingReview(b.ref);
    try {
      const { data } = await api.post(`/bookings/${b.ref}/review-email`);
      setBookings((list) => list.map((x) => (x.ref === b.ref ? data : x)));
      toast.success(`Sent to ${b.email}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send the email"); }
    finally { setSendingReview(null); }
  };
  const [loadingData, setLoadingData] = useState(false);
  const loadAll = async () => {
    setLoadingData(true);
    try {
      const [b, e, t] = await Promise.all([api.get("/bookings"), api.get("/enquiries"), api.get("/enquiries/tours")]);
      setBookings(b.data); setEnquiries(e.data); setTours(t.data);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Session expired, please log in again"); onLogout(); }
      else toast.error("Could not load data");
    } finally { setLoadingData(false); }
  };
  useEffect(() => { loadAll(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [shown, filterUi] = useBookingFilter(bookings);
  const removeBooking = (ref) => setBookings((list) => list.filter((x) => x.ref !== ref));
  const Empty = () => <p className="py-10 text-center text-[#202522]/55">Nothing here yet.</p>;
  return (
    <div data-testid="admin-page">
      <SimpleHero eyebrow="Admin" title="Dashboard" sub="Bookings and hourly enquiries from the website." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
        <div className="flex justify-end gap-2 mb-5">
          <button onClick={loadAll} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-[#183C35]/20 px-4 py-2 text-[14px] text-[#183C35] hover:bg-white transition-colors"><RefreshCw className={`w-4 h-4 ${loadingData ? "animate-spin" : ""}`} />Refresh</button>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-4 py-2 text-[14px] hover:bg-[#21524A] transition-colors"><LogOut className="w-4 h-4" />Log out</button>
        </div>
        <MoneySummary bookings={bookings} />
        <Tabs defaultValue="rides">
          <TabsList className="bg-white border border-[#183C35]/10 rounded-2xl sm:rounded-full p-1 h-auto flex flex-wrap justify-start">
            <TabsTrigger value="rides" className="rounded-full px-4 py-2 data-[state=active]:bg-[#183C35] data-[state=active]:text-[#F7F3EA]" data-testid="tab-rides">Upcoming rides</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-full px-4 py-2 data-[state=active]:bg-[#183C35] data-[state=active]:text-[#F7F3EA]">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="hourly" className="rounded-full px-4 py-2 data-[state=active]:bg-[#183C35] data-[state=active]:text-[#F7F3EA]">Hourly enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="messages" className="rounded-full px-4 py-2 data-[state=active]:bg-[#183C35] data-[state=active]:text-[#F7F3EA]">Messages ({messages.length})</TabsTrigger>
            <TabsTrigger value="tours" className="rounded-full px-4 py-2 data-[state=active]:bg-[#183C35] data-[state=active]:text-[#F7F3EA]">Tour enquiries ({tours.length})</TabsTrigger>
            <TabsTrigger value="codes" className="rounded-full px-4 py-2 data-[state=active]:bg-[#183C35] data-[state=active]:text-[#F7F3EA]" data-testid="tab-codes">Discount codes</TabsTrigger>
            <TabsTrigger value="content" className="rounded-full px-4 py-2 data-[state=active]:bg-[#183C35] data-[state=active]:text-[#F7F3EA]" data-testid="tab-content">Website texts &amp; photos</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings" className="mt-6 space-y-3">
            {filterUi}
            {shown.length === 0 ? <Empty /> : shown.map((b) => (
              <div key={b.ref} className="bg-white rounded-xl border border-[#183C35]/10 p-4 sm:p-5 grid sm:grid-cols-4 gap-3 text-[14.5px]" data-testid={`booking-row-${b.ref}`}>
                <div><p className="font-semibold text-[#183C35]">{b.ref}</p><p className="text-[#202522]/60">{b.name}</p><p className="text-[#202522]/60">{b.email} · {b.phone}</p></div>
                <div className="sm:col-span-2"><p>{b.pickup}</p><p className="text-[#202522]/60">→ {b.dropoff || `${b.hours} hours`}</p></div>
                <div className="sm:text-right"><p className="font-serif-d text-[20px] text-[#183C35]">{b.type === "hourly" ? "On request" : `€${b.total}`}</p>{b.discount ? <p className="text-[12px] text-[#1C7A43]" data-testid={`discount-${b.ref}`}>-€{b.discount} with {b.promo_code}</p> : null}<p className="text-[#202522]/60">{b.date} {b.time}</p><p className="text-[12px] text-[#202522]/50">Emails: client {b.client_email_status} · you {b.admin_email_status}{b.review_email_status ? ` · review ${b.review_email_status}` : " · review not sent"}</p>
                  {b.review_email_status === "sent" ? (
                    <p className="mt-2 text-[12px] text-[#1C7A43]" data-testid={`review-sent-${b.ref}`}>Review email sent{b.review_promo_code ? ` · ${b.review_promo_code}` : ""}</p>
                  ) : (
                    <button onClick={() => sendReview(b)} disabled={sendingReview === b.ref} data-testid={`send-review-${b.ref}`}
                      className="mt-2 inline-flex rounded-full border border-[#183C35]/20 px-3 py-1 text-[12px] text-[#183C35] hover:bg-[#F6F2E9] transition-colors disabled:opacity-50">
                      {sendingReview === b.ref ? "Sending…" : "Send review & discount email"}
                    </button>
                  )}
                  {b.pay === "card" && (
                    <div className="mt-2 flex sm:justify-end">
                      <span className={`text-[12px] px-2 py-0.5 rounded-full font-medium ${b.payment_status === "paid" ? "bg-[#E3F4EA] text-[#1C7A43]" : "bg-[#FDE8E8] text-[#B42318]"}`} data-testid={`card-status-${b.ref}`}>Card: {b.payment_status === "paid" ? "Paid · confirmed" : "Not paid · not confirmed"}</span>
                    </div>
                  )}
                  {b.pay === "online" && (
                    <div className="mt-2 flex sm:justify-end items-center gap-2">
                      <span className={`text-[12px] px-2 py-0.5 rounded-full font-medium ${b.payment_status === "paid" ? "bg-[#E3F4EA] text-[#1C7A43]" : "bg-[#FFF3D6] text-[#8A5A00]"}`} data-testid={`pay-status-${b.ref}`}>PayPal: {b.payment_status === "paid" ? "Paid" : "Awaiting"}</span>
                      {b.payment_status === "paid" && b.receipt_email_status && <span className="text-[11.5px] text-[#202522]/55" data-testid={`receipt-status-${b.ref}`}>Receipt {b.receipt_email_status}</span>}
                      <button onClick={() => togglePaid(b)} data-testid={`toggle-paid-${b.ref}`} className="text-[12px] underline text-[#3E7F8E] hover:text-[#183C35]">{b.payment_status === "paid" ? "Mark unpaid" : "Mark paid"}</button>
                    </div>
                  )}
                  <div className="sm:flex sm:justify-end"><DeleteBookingButton b={b} onDeleted={removeBooking} /></div>
                </div>
              </div>
            ))}
          </TabsContent>
          <TabsContent value="hourly" className="mt-6 space-y-3">
            {enquiries.length === 0 ? <Empty /> : enquiries.map((m) => (
              <div key={m.id} className="bg-white rounded-xl border border-[#183C35]/10 p-5 text-[14.5px]"><p className="font-semibold text-[#183C35]">{m.pickup} · {m.hours} hours</p><p className="text-[#202522]/60">{m.date} {m.time} · {m.pax} passengers · {m.vehicle || "Any vehicle"}</p></div>
            ))}
          </TabsContent>
          <TabsContent value="messages" className="mt-6 space-y-3">
            {messages.length === 0 ? <Empty /> : messages.map((m, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#183C35]/10 p-5 text-[14.5px]"><p className="font-semibold text-[#183C35]">{m.name} · {m.email}</p><p className="text-[#202522]/60">{m.subject}</p><p className="mt-2">{m.message}</p></div>
            ))}
          </TabsContent>
          <TabsContent value="tours" className="mt-6 space-y-3">
            {tours.length === 0 ? <Empty /> : tours.map((m, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#183C35]/10 p-5 text-[14.5px]"><p className="font-semibold text-[#183C35]">{m.tour}</p><p className="text-[#202522]/60">{m.date || "Flexible date"} · {m.guests} guests · email {m.admin_email_status}</p></div>
            ))}
          </TabsContent>
          <TabsContent value="codes" className="mt-6"><PromoCodesPanel /></TabsContent>
          <TabsContent value="rides" className="mt-6"><RidesPanel bookings={bookings} setBookings={setBookings} /></TabsContent>
          <TabsContent value="content" className="mt-6"><ContentEditor /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!u || !p) return setErr("Please enter your username and password.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { username: u, password: p });
      setToken(data.token);
      onLogin();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Login failed. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl border border-[#183C35]/10 shadow-[0_30px_60px_-30px_rgba(24,60,53,.35)] p-8 fade-up" data-testid="admin-login-form">
        <span className="w-12 h-12 rounded-xl bg-[#183C35] text-[#F7F3EA] flex items-center justify-center"><Lock className="w-5 h-5" /></span>
        <h1 className="font-serif-d text-[32px] font-semibold text-[#183C35] mt-5">Admin login</h1>
        <p className="text-[15px] text-[#202522]/65 mt-1">Bookings, enquiries and payments.</p>
        <label className="block text-[12px] tracking-[0.08em] uppercase font-medium text-[#3E7F8E] mt-7 mb-2">Username</label>
        <Input className="h-12 rounded-xl" autoComplete="username" value={u} onChange={(e) => setU(e.target.value)} data-testid="admin-username" />
        <label className="block text-[12px] tracking-[0.08em] uppercase font-medium text-[#3E7F8E] mt-5 mb-2">Password</label>
        <div className="relative">
          <Input className="h-12 rounded-xl pr-11" type={show ? "text" : "password"} autoComplete="current-password" value={p} onChange={(e) => setP(e.target.value)} data-testid="admin-password" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#202522]/50 hover:text-[#183C35]" aria-label="Show password">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
        </div>
        {err && <p className="mt-4 text-[14px] text-[#B42318]" data-testid="admin-login-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-btn" className="mt-6 w-full h-12 rounded-xl bg-[#183C35] text-[#F7F3EA] font-semibold hover:bg-[#21524A] disabled:opacity-60 transition-colors">{busy ? "Signing in..." : "Sign in"}</button>
      </form>
    </div>
  );
};

export const Admin = () => {
  const [authed, setAuthed] = useState(!!getToken());
  useEffect(() => {
    if (getToken()) api.get("/admin/me").catch(() => { setToken(null); setAuthed(false); });
  }, []);
  const logout = () => { setToken(null); setAuthed(false); };
  return authed ? <AdminDashboard onLogout={logout} /> : <AdminLogin onLogin={() => setAuthed(true)} />;
};
